import "./Settings.scss";
import React, { useState, useEffect } from "react";
import {
  IonList,
  IonLabel,
  IonItem,
  IonToggle,
  IonPage,
  IonContent,
  isPlatform,
  IonLoading,
} from "@ionic/react";
import { AuthMode, VaultErrorCodes } from "@ionic-enterprise/identity-vault";
import { useTracked } from "../../state";
import { identityService } from "../../services/vault";
import { loadAuthMode } from "../../context/vault";
import { Plugins } from "@capacitor/core";
import PrivateHeader from "../../components/PrivateHeader";
import MockDelay from "../../utils/helpers/MockDelay";
import { ShowToastErrorMessage } from "../../utils/helpers/ToastMessage";
// import {
//   ShowToastErrorMessage,
//   ShowToastSuccessMessage,
// } from "../../utils/helpers/ToastMessage";
const { Storage } = Plugins;

interface Props {
  authMode: any;
  biometricType: any;
  biometricsSupported: any;
  isOnlyPin: any;
  showPinDialog: any;
}

const Settings: React.FC<Props> = React.memo(
  ({
    authMode,
    biometricType,
    biometricsSupported,
    showPinDialog,
    isOnlyPin,
  }) => {
    const [, dispatch] = useTracked();
    const [passcode, setPasscode] = useState<boolean>(false);
    const [biometrics, setBiometrics] = useState<boolean>(false);
    const [secureStorge, setSecureStorge] = useState<boolean>(false);
    const [isBiometricsAvailable, setIsBiometricsAvailable]= useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
      updateVaultAuth();

      const checkBiometricsAvailability = async () => {
        const isBioAvailable = await identityService.isBiometricsAvailable();
        setIsBiometricsAvailable(isBioAvailable);
      }
      checkBiometricsAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      setPasscode(
        authMode === AuthMode.PasscodeOnly ||
          authMode === AuthMode.BiometricAndPasscode
      );
      setBiometrics(
        authMode === AuthMode.BiometricOnly ||
          authMode === AuthMode.BiometricAndPasscode
      );
      setSecureStorge(authMode === AuthMode.SecureStorage);
    }, [authMode]);

    useEffect(() => {
      if (
        localStorage.getItem("isOnlyPin") === "false" &&
        authMode !== AuthMode.PasscodeOnly &&
        authMode !== AuthMode.BiometricOnly &&
        authMode !== AuthMode.BiometricAndPasscode
      ) {
        toggleUseSecureStorage();
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnlyPin, showPinDialog]);

    const updateVaultAuth = async () => {
      const { authMode, biometricType, hasSession, biometricsSupported } =
        await loadAuthMode();
  
      dispatch({
        type: "setVaultAuth",
        authMode,
        biometricType,
        hasSession,
        biometricsSupported,
      });
    };

    const toggleUseBiometrics = async () => {
      if (!biometrics) {
        setBiometrics(true);
        await identityService.setAuthMode(
          passcode ? AuthMode.BiometricAndPasscode : AuthMode.BiometricOnly
        );
        // make to remove
        localStorage.removeItem("isAppLocked");
        await updateVaultAuth();
      } else {
        setBiometrics(false);
        await identityService.setAuthMode(
          passcode ? AuthMode.PasscodeOnly : AuthMode.SecureStorage
        );
        await updateVaultAuth();
      }
    };

    const setPIN = async () => {
      // requires pin if is set.
      try {
        setIsLoading(true);
        const vault = await identityService.getVault();
        const isPasscodeEnabled = await vault.isPasscodeEnabled();

        if (isPasscodeEnabled) {
          await Storage.set({ key: "isLockFromSwitchAccount", value: "true" });

          await identityService.lockOut();
          await MockDelay(3000);
          setIsLoading(false);

          await identityService.unlock(AuthMode.PasscodeOnly);
          const session = await identityService.restoreSession();

          if (session && session.token && session.identity) {
          } else {
            ShowToastErrorMessage("Authentication failed.");
          }

          dispatch({ type: "hideApplicationsPIN" });
          dispatch({ type: "setPINFailedAttempts", value: 0 });

          await Storage.remove({ key: "isLockFromSwitchAccount" });
        } else {
          setIsLoading(false);
        }
      } catch (e) {
        console.log(e);

        dispatch({ type: "hideApplicationsPIN" });
        dispatch({ type: "setPINFailedAttempts", value: 0 });

        if ((e as any).code === VaultErrorCodes.TooManyFailedAttempts) {
          ShowToastErrorMessage("Too many failed attempts. Try again later.");
        } else {
          ShowToastErrorMessage("Invalid PIN.");
        }

        await Storage.remove({ key: "isLockFromSwitchAccount" });
        return;
      }

      if (!passcode) {
        localStorage.setItem("isOnlyPin", JSON.stringify(true));
        dispatch({ type: "showApplicationsPIN", setApplicationsPin: true });
        localStorage.removeItem("isAppLocked");
      } else {
        setPasscode(false);
        localStorage.setItem("isOnlyPin", JSON.stringify(false));
        await identityService.setAuthMode(
          biometrics ? AuthMode.BiometricOnly : AuthMode.SecureStorage
        );
        await updateVaultAuth();
      }
    };

    const toggleUseSecureStorage = async () => {
      if (!secureStorge) {
        setPasscode(false);
        setBiometrics(false);
        setSecureStorge(!secureStorge);
        await identityService.setAuthMode(AuthMode.SecureStorage);

        await updateVaultAuth();
        // clears passcode pin to open modal when passcode mode selected
        await Storage.set({ key: "enteredPincode", value: "" });
      }
    };

    const getBiometricTypeLabel = () => {
      if (biometricType === "touchID") {
        return "Touch ID";
      } else if (biometricType === "faceID") {
        return "Face ID";
      } else {
        return "Biometrics (None)";
      }
    };

    return (
      <IonPage className="settings-page">
        <PrivateHeader title="Settings" />

        <IonContent>
          <IonList>
            {isPlatform("cordova") && (
              <IonItem lines="none">
                <IonLabel>Enable Application PIN</IonLabel>
                <IonToggle
                  checked={passcode}
                  onClick={setPIN}
                  color="success"
                />
              </IonItem>
            )}

            {biometricsSupported && (
              <IonItem lines="none">
                <IonLabel>Unlock with {getBiometricTypeLabel()}</IonLabel>
                <IonToggle
                  checked={biometrics}
                  disabled={!isBiometricsAvailable}
                  onClick={toggleUseBiometrics}
                  color="success"
                />
              </IonItem>
            )}

            {!isPlatform("cordova") && (
              <IonItem lines="none">
                <IonLabel>Secure Storage Mode</IonLabel>
                <IonToggle
                  checked={secureStorge}
                  disabled={secureStorge}
                  onIonChange={toggleUseSecureStorage}
                  color="success"
                />
              </IonItem>
            )}
            {/* <IonItem lines="none">
              <IonLabel>App Version</IonLabel>
              <IonNote slot="end">{p.version}</IonNote>
            </IonItem> */}
          </IonList>

          <IonLoading isOpen={isLoading} />
        </IonContent>
      </IonPage>
    );
  }
);

const MemoizedSettings = () => {
  const [state] = useTracked();
  const {
    authMode,
    biometricType,
    biometricsSupported,
    isOnlyPin,
    showPinDialog,
  } = state;
  return (
    <Settings
      authMode={authMode}
      biometricType={biometricType}
      biometricsSupported={biometricsSupported}
      isOnlyPin={isOnlyPin}
      showPinDialog={showPinDialog}
    />
  );
};

export default MemoizedSettings;
